import React, { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { Button, Checkbox, Dimmer, Divider, Form, Header, Loader, Message, Segment } from "semantic-ui-react";
import { countriesAtom, currenciesAtom, languagesAtom, productsByIdAtom } from "../state.js";
import { cartAtom } from "../atoms/cartAtom";
import Title from "../components/Title";
import AdminOrderPaymentSlug from "./AdminOrderPaymentSlug.js";

const AdminCartToOrder = ({}) => {
  const [cart, setCart] = useAtom(cartAtom);
  const [countries] = useAtom(countriesAtom);
  const [currencies] = useAtom(currenciesAtom);
  const [languages] = useAtom(languagesAtom);
  const [productsById] = useAtom(productsByIdAtom);

  const swedenID = Object.values(countries).find((country) => country.name === "Sweden")?.id;
  const sekID = Object.values(currencies).find((currency) => currency.iso4217 === "SEK")?.id;
  const swedishID = Object.values(languages).find((language) => language.name === "Swedish")?.id;

  const [allocateStock, setAllocateStock] = useState(false);
  const [emptyCart, setEmptyCart] = useState(true);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentSlug, setPaymentSlug] = useState("");
  const [selectedCountryID, setSelectedCountryID] = useState(String(swedenID));
  const [selectedCurrencyID, setSelectedCurrencyID] = useState(String(sekID));
  const [selectedLanguageID, setSelectedLanguageID] = useState(String(swedishID));

  const invalidCart = (cart?.items || []).some((item) => {
    const product = productsById[item.id];
    if (!product) return true;
    return product.stock < item.quantity || product.published !== true;
  });

  const countryOptions = Object.values(countries).map((country) => ({
    key: String(country.id),
    text: country.name,
    value: String(country.id),
  }));

  const currencyOptions = Object.values(currencies).map((currency) => ({
    key: String(currency.id),
    text: currency.iso4217,
    value: String(currency.id),
  }));

  const languageOptions = Object.values(languages).map((language) => ({
    key: String(language.id),
    text: language.name,
    value: String(language.id),
  }));

  useEffect(() => {
    if (swedenID) setSelectedCountryID(String(swedenID));
    if (sekID) setSelectedCurrencyID(String(sekID));
    if (swedishID) setSelectedLanguageID(String(swedishID));
  }, [swedenID, sekID, swedishID]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (invalidCart) return;

    setLoading(true);

    const response = await fetch("/api/cart-to-order", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        allocate_stock: allocateStock,
        country_id: Number(selectedCountryID),
        currency_id: Number(selectedCurrencyID),
        language_id: Number(selectedLanguageID),
        cart: cart?.items?.map((item) => ({
          product_id: item.id,
          quantity: item.quantity,
        })),
      }),
    });

    if (response.ok) {
      const data = await response.json();
      if (!data.payment_slug) {
        setError(true);
        setLoading(false);
        return;
      }

      if (emptyCart) {
        setCart({ items: [], checkingOut: false });
      }

      setPaymentSlug(data.payment_slug);
    }

    setLoading(false);
  };

  if (error) {
    return (
      <Message negative size="big">
        <Message.Header>Något gick fel</Message.Header>
        <Message.Content>Vänligen försök igen senare</Message.Content>
      </Message>
    );
  }

  if (paymentSlug) {
    return <AdminOrderPaymentSlug payment_slug={paymentSlug} />;
  }

  return (
    <>
      <Title title="Korg till kundorder" />

      <br />

      <Segment>
        <Dimmer active={loading} inverted>
          <Loader size="big" active={loading} />
        </Dimmer>
        <Form>
          <Form.Dropdown
            placeholder="Country"
            search
            selection
            options={countryOptions}
            value={selectedCountryID}
            onChange={(e, { value }) => setSelectedCountryID(String(value))}
          />

          <Form.Dropdown
            placeholder="Currency"
            search
            selection
            options={currencyOptions}
            value={selectedCurrencyID}
            onChange={(e, { value }) => setSelectedCurrencyID(String(value))}
          />

          <Form.Dropdown
            placeholder="Language"
            search
            selection
            options={languageOptions}
            value={selectedLanguageID}
            onChange={(e, { value }) => setSelectedLanguageID(String(value))}
          />

          <Form.Checkbox
            checked={allocateStock}
            label="Reservera lager"
            onChange={(e, { checked }) => setAllocateStock(checked)}
          />

          <Form.Checkbox
            checked={emptyCart}
            label="Töm kundvagn"
            onChange={(e, { checked }) => setEmptyCart(checked)}
          />

          {invalidCart && (
            <Message negative>
              <Message.Header>Varning!</Message.Header>
              <p>
                Någon eller flera produkter i kundvagnen har inte tillräckligt med lager eller är inte publicerade.
                Vänligen kontrollera produkterna innan ordern skapas.
              </p>
            </Message>
          )}

          <Form.Button size="big" positive type="submit" disabled={invalidCart} onClick={handleSubmit}>
            Skapa order
          </Form.Button>
        </Form>
      </Segment>
    </>
  );
};

export default AdminCartToOrder;
